import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import clsx from 'clsx';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { Strong, Text } from '@clay-run/glaze/text';
import type { EnrollmentItem, InputFieldOptions, QueueResponse, SenderOption } from '@shared/flow/enrollment';
import type { ActivePlayType, PlayOption } from '@shared/flow/play-catalog';
import type { GongFlowSummary } from '@shared/flow/gong-flow-catalog';

import { FLOW_ICONS } from '../../icons';
import { BaseCopyEditor } from '../../EnrollQueue/BaseCopyEditor';
import type { BaseCopyDraftStore, DefaultSenderState, ItemAutosaveOwnership, ItemAutosavePatch } from '../../EnrollQueue/enroll-queue-types';
import type { CopyPrefill } from '../../EnrollQueue/EnrollmentItemEditor';
import { GroupActionConfirmDialog } from './GroupActionConfirmDialog';
import { deriveGroupSendability } from '../../EnrollQueue/queue-group-send';
import { resolveGroupName } from '../../EnrollQueue/queue-derive';
import {
  mergeStagedCopyPrefill,
  playConfigKey,
  useStagedBaseCopy,
  useStagedBaseSender,
  useStagedSenderTouched,
  useStagedStepCopy,
} from '../../EnrollQueue/state/staged-play-config';
import type { StepOverrideSnapshot } from '../../EnrollQueue/StepOverrideEditor';
import type { DockSendHandlers } from '../../EnrollQueue/useDockSendHandlers';
import { BulkEditor } from './BulkEditor';
import { LeadRow } from './LeadRow';
import { RAIL_ROW_DIVIDER, RAIL_ROW_INSET_Y, RAIL_ROW_STRIP_MIN_H, RAIL_ROW_SURFACE } from './RailRow';

/**
 * One play's section in the V2 rail's Plays pane (plan 2026-09-30-002, U2c).
 *
 * This is the V2 counterpart of `PlayQueueCard`, and it deliberately keeps
 * that component's prop *names* so `PlaysPane` (U2d) maps its
 * `useEnrollQueue()` reads across one-for-one. The V1 file is NOT edited and
 * NOT reused as a component: its chrome is a Glaze `CollapsibleCard` with a
 * nested toggle `Button`, where D2 specifies a borderless, sticky
 * `min-h-16` header strip. What IS reused, never re-derived (D30):
 * `resolveGroupName`, `deriveGroupSendability`, `mergeStagedCopyPrefill`,
 * the `state/staged-play-config` subscriptions, `BaseCopyEditor` and
 * `GroupActionConfirmDialog`. The batch step rung is the sibling V2
 * component `BulkEditor` (U3), which drives the SAME `useBatchStepBuffer`
 * the V1 batch step-defaults editor does.
 *
 * Sectioning/keying is the PARENT's job: `ByPlay` renders one of these per
 * assigned group with `key={`${play_type_id}:${play_instance_ref}`}`, which
 * is also what makes D33's `open` state reset when the rep flips By Play ↔
 * By Contact (the whole subtree unmounts) and what keeps two instances of
 * the same play type on different instance refs independent.
 */
export interface PlaySectionProps {
  group: QueueResponse['groups'][number];
  /**
   * The items to RENDER — i.e. `group.items` after `PlaysPane`'s D36 filter,
   * not the raw group. Every count and every derivation in this component
   * follows this array (E65: a filtered section's `Send all`/`Clear all`
   * count the visible leads), never `group.items`.
   */
  items: EnrollmentItem[];
  /** D2 — the first section in the list carries no top hairline. */
  isFirst: boolean;
  /** D39 — the dock's ONE `useDockSendHandlers()` object, threaded down. */
  handlers: DockSendHandlers;
  readOnly: boolean;
  /** Gates Send controls only; defaults to `readOnly`. Edit/remove controls stay on `readOnly`. */
  sendReadOnly?: boolean;
  /** E35 — source of the header's play-type badge; an aged-out type simply renders no badge. */
  activePlayTypes: ActivePlayType[];
  /** `EnrollQueueContext.plays` — read only for this play's `invite_subject`/`invite_body`. */
  plays: PlayOption[];
  senders: SenderOption[];
  defaultSender: DefaultSenderState;
  baseCopyDraftStore: BaseCopyDraftStore;
  sendSkipLabelFor: (itemId: number) => string | null;
  isItemBusy: (itemId: number) => boolean;
  /** `EnrollQueueContext.isSending` — OR'ed with `handlers.groupSendPending` for each row's `sending`. */
  sending: boolean;
  /** `EnrollQueueContext.bulkRemove` — only ever called from the Clear dialog's confirm. */
  onBulkRemove: (itemIds: number[]) => Promise<void>;

  // ── Auto-open / landing signals (D20, E41, E54) ────────────────────────
  /**
   * `queuePlayLanding.nonce` when the landing targets THIS play, else null
   * (the parent does the matching, same posture as `PlayQueueCard`'s
   * `landing` prop). Non-null → the section opens and scrolls itself to the
   * top of the rail scroller, then reports back via `onLandingScrolled` so
   * the pane can `dismissQueuePlayLanding(nonce)`.
   */
  landingKey?: number | null;
  /**
   * `queueFlashRequest` itself — opens this section when the flashed contact
   * is one of its items. The NONCE is part of the prop on purpose (G2): two
   * consecutive flashes for the same contact are indistinguishable by
   * `contactId` alone, and this shape makes a nonce-less flash
   * unrepresentable. `QueueFlashRequest` is structurally assignable to it.
   */
  flash?: { contactId: string; nonce: number } | null;
  /** `handlers.focusContactId` — same, durable rather than consumed-on-scroll. */
  focusContactId?: string | null;
  onLandingScrolled?: (landingKey: number) => void;

  // ── Lifted row expansion (D19) ─────────────────────────────────────────
  expandedItemId: number | null;
  onToggleExpanded: (itemId: number) => void;

  // ── Per-row plumbing, threaded straight to `LeadRow` ───────────────────
  inputOptionsFor?: (itemId: number) => InputFieldOptions[];
  onSelectInputOption?: (itemId: number, fieldKey: string, persistsTo: InputFieldOptions['persistsTo'], value: string | null) => Promise<void>;
  onRefreshInputOptionsIfStale?: (itemId: number) => void;
  onSenderTouched?: (itemId: number) => void;
  onAutosave: (itemId: number, patch: ItemAutosavePatch, owned: ItemAutosaveOwnership) => void;
  onFlushSave: (itemId: number) => Promise<void>;
  onEditStepOverride?: (itemId: number, stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  onResetStepOverride?: (itemId: number, stepNumber: number, stepType: string) => void;
  stepOverrideDraftFor?: (itemId: number, stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  stepSaveErrorFor?: (itemId: number, stepNumber: number) => string | null;
  subscribeStepOverrides?: (itemId: number, stepNumber: number, onChange: () => void) => () => void;
  onSaveStepOverrides: (itemId: number) => Promise<void>;
  onBeginSendPipeline: (itemIds: number[]) => boolean;
  onEndSendPipeline: (itemIds: number[]) => void;
  /** PM1 / terra-ohe.8 — see `LeadRow`'s matching prop docs; optional, threaded unchanged. */
  onUnstick?: (itemId: number) => void;
  unstickPending?: boolean;
  onCancel?: (itemId: number) => void;
  cancelPending?: boolean;

  // ── U4b — footer plumbing, threaded straight to `LeadRow`/`LeadExpandedBody` ──
  /** `EnrollQueueContext.gongFlowsSuggested` — see `LeadEditorContext`'s matching prop doc. */
  gongFlowsSuggested?: GongFlowSummary[];
  /** `EnrollQueueContext.assignPlay` — D41's `onReassign`. */
  onReassign?: (itemId: number, play: PlayOption) => void | Promise<void>;
  /** `EnrollQueueContext.clearStepOverridesForItem` — D41's `onClearStepOverrides`. */
  onClearStepOverrides?: (itemId: number) => () => void;
  /** `EnrollQueueContext.requestQueueFlash` — D41's post-reassign flash cue. */
  requestQueueFlash?: (contactId: string, opts?: { autoOpenPicker?: boolean; openEditorAfterAssign?: boolean }) => void;
  /** `EnrollQueueContext.removeItem` — the expanded body's `Remove` action. */
  onRemove?: (itemId: number) => void;
}

export function PlaySection({
  group,
  items,
  isFirst,
  handlers,
  readOnly,
  sendReadOnly,
  activePlayTypes,
  plays,
  senders,
  defaultSender,
  baseCopyDraftStore,
  sendSkipLabelFor,
  isItemBusy,
  sending,
  onBulkRemove,
  landingKey = null,
  flash = null,
  focusContactId = null,
  onLandingScrolled,
  expandedItemId,
  onToggleExpanded,
  inputOptionsFor,
  onSelectInputOption,
  onRefreshInputOptionsIfStale,
  onSenderTouched,
  onAutosave,
  onFlushSave,
  onEditStepOverride,
  onResetStepOverride,
  stepOverrideDraftFor,
  stepSaveErrorFor,
  subscribeStepOverrides,
  onSaveStepOverrides,
  onBeginSendPipeline,
  onEndSendPipeline,
  onUnstick,
  unstickPending,
  onCancel,
  cancelPending,
  gongFlowsSuggested,
  onReassign,
  onClearStepOverrides,
  requestQueueFlash,
  onRemove,
}: PlaySectionProps) {
  const panelId = useId();
  const rootRef = useRef<HTMLElement | null>(null);
  // D33 — section open/closed is LOCAL and starts closed. Never seeded from
  // a query (Draft State table, Tier 1).
  const [open, setOpen] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const name = resolveGroupName(group);
  const playTypeId = group.play_type_id as number;
  const playInstanceRef = group.play_instance_ref as string;
  const play = plays.find(p => p.play_type_id === playTypeId && p.play_instance_ref === playInstanceRef);

  // ── Staged play config: the same narrow, per-play subscriptions
  //    `PlayQueueCard` takes (D24 fix 1), so a write to ANOTHER play's
  //    staged copy never re-renders this section. ────────────────────────
  const playKey = playConfigKey(playTypeId, playInstanceRef);
  const baseDraft = useStagedBaseCopy(baseCopyDraftStore.identity, playKey);
  const copyPrefill: CopyPrefill = useMemo(() => mergeStagedCopyPrefill(baseDraft, {
    subject: play?.invite_subject ?? null,
    body: play?.invite_body ?? null,
  }), [baseDraft, play?.invite_subject, play?.invite_body]);
  const stagedSender = useStagedBaseSender(baseCopyDraftStore.identity, playKey);
  const senderTouchedIds = useStagedSenderTouched(baseCopyDraftStore.identity);
  const stagedStepCopy = useStagedStepCopy(baseCopyDraftStore.identity, playKey);

  const senderPrefillFor = (item: EnrollmentItem): string | null =>
    senderTouchedIds.includes(item.id) ? null : stagedSender ?? null;
  /** `PlayQueueCard`'s staged-sender resolution, verbatim — "what this row will actually send as". */
  const effectiveSenderFor = (item: EnrollmentItem): string | null =>
    senderPrefillFor(item) ?? item.sender;
  /** `PlayQueueCard`'s `resetBaseSenderFor`, verbatim (it refuses to write null over a working sender on an UNRESOLVED default). */
  const resetBaseSenderFor = (item: EnrollmentItem): string | null =>
    stagedSender
    ?? (defaultSender.resolved ? defaultSender.sender : item.sender)
    ?? null;

  // Group-sourced capabilities, same fail-closed posture as `PlayQueueCard`.
  const hasEditableCopy = group.has_editable_copy ?? false;
  const copyRequired = group.copy_required ?? false;
  const gongFlowId = group.ui?.has_step_overrides ? playInstanceRef : null;
  const bodyFormat = group.ui?.body_format ?? 'plain';

  // D30 — the ONE sendability derivation in the V2 Plays pane's By Play view.
  // Every row's pill/`title`/disabled-Send comes from this call's
  // `blockLabels[i]`; nothing below re-derives it.
  const sendGate = sendReadOnly ?? readOnly;
  const { blockLabels, sendableIds, sendableCount } = deriveGroupSendability(items, sendGate, sendSkipLabelFor, effectiveSenderFor);
  const groupBusy = sendableIds.some(id => isItemBusy(id));
  const skippedCount = items.length - sendableCount;
  const playTypeName = activePlayTypes.find(t => t.play_type_id === group.play_type_id)?.play_type_name ?? null;

  /**
   * D20 / E54 / F4 / G2 — auto-open on a landing / flash / focus signal,
   * adopted DURING RENDER rather than in a passive effect.
   *
   * Why render-phase and not `useEffect`: `PlaysPane`'s flash effect is a
   * passive effect that does ONE `root.querySelector('[data-flash-contact=…]')`
   * and falls through to `consumeQueueFlashRequest(nonce)` when it finds
   * nothing. Passive effects flush child-first, so a `setOpen(true)` of ours
   * in an effect would still only have SCHEDULED a re-render by the time the
   * pane's effect ran — the `LeadRow`s would not be in the DOM yet, the pane
   * would take its "missing row" branch, and the rep would get a section that
   * opened but never scrolled or pulsed. Sections mount CLOSED and render no
   * rows while closed, so this is the common case: assign a contact to a
   * previously empty play. (A `PlaysPane`-side "retry next tick" is NOT a fix
   * — a `setTimeout`/rAF races React's own scheduler task for our re-render.)
   *
   * Adopting during render means that in the very commit where a signal first
   * points at this section, `open` is already `true` and the rows are in the
   * DOM for the pane's unchanged effect to find. This is React's documented
   * "adjust state during render" pattern — the same render-phase
   * compare-and-set `useDraftState` uses (`docs/v4-system/draft-state-under-sse.md`).
   *
   * WHAT is adopted is per-signal IDENTITY, in `adopted` STATE — three
   * independent fields, never one derived boolean. G2: the boolean form
   * (`landingKey !== null || contains(flash) || contains(focus)`) collapsed
   * three independent signals into one bit, and a bit has no room for "which
   * one, and which instance of it". Two defects followed directly:
   *  - a NEW target arriving while an OLD signal was still present produced
   *    no false→true edge, so a flash for contact B (or focus moving A→B) on
   *    a section already holding a durable focus on A never opened it, and
   *    `PlaysPane` consumed that flash without ever finding B's row;
   *  - a D36 filter that dropped the focus target flipped the bit to false,
   *    and clearing the filter flipped it back — REPLAYING an already-handled
   *    focus over a rep who had deliberately collapsed the section.
   *
   * Per-signal adoption rules, and why each is shaped the way it is:
   *  - `landing`: adopt when `landingKey` is non-null and differs from the
   *    adopted nonce. NEVER reset — `useQueueUiSignals`' `queueFlashNonceRef`
   *    is a single monotonic counter shared by the flash and landing
   *    producers, so every nonce it hands out is unique across BOTH and an
   *    adopted nonce can never recur.
   *  - `flash`: same nonce rule, which is exactly why the prop carries the
   *    nonce (`flash: { contactId, nonce }`) rather than a bare contact id —
   *    two consecutive flashes for ONE contact are indistinguishable by id,
   *    and the shape makes a nonce-less flash unrepresentable. Gated on the
   *    contact actually being in `items`: a target the D36 filter is hiding
   *    is NOT adopted, so it stays adoptable if the filter later admits it
   *    while the request is still live (E63's pane-side consume usually gets
   *    there first, which is the documented degrade).
   *  - `focus`: `handlers.focusContactId` is DURABLE — it survives every
   *    queue refresh — so it is adopted by contact id and reset to null only
   *    when the prop itself goes null. That mirrors `PlaysPane`'s
   *    `focusHandledRef` one-for-one and is what preserves "A → null → A
   *    re-opens". It is deliberately NOT reset when the id is unchanged but
   *    merely filtered out of `items`: that is the stale-replay defect above.
   *
   * Only ever `setOpen(true)`, and only when closed — a rep who collapses a
   * section is never fought by a still-present signal, because adoption
   * already happened and nothing re-adopts the same identity.
   *
   * StrictMode-safe by construction (E54): the compare-and-set is pure and
   * convergent — after the write, every branch's condition is false and
   * `open` is true, so a double-invoked render performs no further writes.
   * It owns no lifetime ref to reset.
   */
  const containsContact = (contactId: string): boolean =>
    items.some(item => item.contact_id === contactId);

  const [adopted, setAdopted] = useState<{
    landing: number | null;
    flash: number | null;
    focus: string | null;
  }>({ landing: null, flash: null, focus: null });

  let nextAdopted = adopted;
  let adoptsNewSignal = false;
  if (landingKey !== null && landingKey !== adopted.landing) {
    nextAdopted = { ...nextAdopted, landing: landingKey };
    adoptsNewSignal = true;
  }
  if (flash !== null && flash.nonce !== adopted.flash && containsContact(flash.contactId)) {
    nextAdopted = { ...nextAdopted, flash: flash.nonce };
    adoptsNewSignal = true;
  }
  if (focusContactId === null) {
    if (adopted.focus !== null) nextAdopted = { ...nextAdopted, focus: null };
  } else if (focusContactId !== adopted.focus && containsContact(focusContactId)) {
    nextAdopted = { ...nextAdopted, focus: focusContactId };
    adoptsNewSignal = true;
  }
  if (nextAdopted !== adopted) setAdopted(nextAdopted);
  if (adoptsNewSignal && !open) setOpen(true);

  /**
   * E41 — the landing scroll targets the SECTION ROOT with `block: 'start'`
   * (a row-level flash uses `block: 'center'` so it clears the sticky
   * header; a landing wants the header itself at the top of the scroller).
   *
   * The ref is a "landing key already handled" marker, NOT lifetime state:
   * it is deliberately never reset (neither at the top of the effect nor in
   * a cleanup). That is what makes StrictMode's setup→cleanup→setup fire the
   * scroll and the `onLandingScrolled` report exactly once, while a genuine
   * remount — a fresh component instance with a fresh ref — still scrolls.
   */
  const scrolledLandingKeyRef = useRef<number | null>(null);
  useEffect(() => {
    if (landingKey === null) return;
    if (scrolledLandingKeyRef.current === landingKey) return;
    scrolledLandingKeyRef.current = landingKey;
    rootRef.current?.scrollIntoView({ block: 'start' });
    onLandingScrolled?.(landingKey);
  }, [landingKey, onLandingScrolled]);

  const toggle = () => setOpen(o => !o);
  const onHeaderKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // F3 — this strip CONTAINS the `Clear all N`/`Send all N` buttons, and a
    // keydown on one of them bubbles here. Without this guard the
    // `preventDefault()` below cancelled the native button's OWN Enter/Space
    // activation, so a keyboard rep's group send never fired and the section
    // toggled instead. (The mouse path was never affected — it goes through
    // the `stopPropagation` wrapper on the button cluster below, which is
    // still required: a button's activation CLICK bubbles here too.)
    //
    // `e.target !== e.currentTarget` is exact rather than approximate: the
    // header strip and those two buttons are the only focusable elements in
    // it, so a keydown that did not originate on the strip itself is by
    // construction a keydown on one of the buttons.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      // This is a `role="button"` div, not a native `<button>`, so Space's
      // default page scroll has to be prevented here.
      e.preventDefault();
      toggle();
    }
  };

  return (
    <section ref={rootRef}>
      {/* D2 — borderless sticky header strip. `min-h-16` (not `h-16`) so a
          2/3-line play name grows the header instead of clipping (E39/E40),
          which is also why the name `Strong` carries `min-w-0 flex-1` and
          NO `truncate`. */}
      <div
        className={clsx(
          'sticky top-0 z-10 bg-bg-primary',
            RAIL_ROW_INSET_Y,
          !isFirst && RAIL_ROW_DIVIDER,
          open && 'border-b-[0.5px] border-border-primary',
        )}
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={toggle}
          onKeyDown={onHeaderKeyDown}
          className={clsx('flex items-center gap-2 px-3', RAIL_ROW_STRIP_MIN_H, RAIL_ROW_SURFACE)}
        >
          <Icon type={FLOW_ICONS.expand} size="sm" className={clsx('shrink-0 transition-transform', open && 'rotate-90')} />
          <Strong className="min-w-0 flex-1 text-sm">
            {name}
            {playTypeName && <Badge color="secondary" size="xs" className="ml-1.5 align-middle">{playTypeName}</Badge>}
          </Strong>
          {/* D2 — one `stopPropagation` on the group rather than one per
              button: the header's own toggle must not fire for any control
              inside this cluster. */}
          <div className="flex shrink-0 items-center gap-1.5" onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <Button
              variant="danger-secondary"
              size="xs"
              disabled={readOnly}
              aria-label={`Clear all ${items.length} for ${name}`}
              onClick={() => setConfirmClearOpen(true)}
            >
              {`Clear all ${items.length}`}
            </Button>
            <Button
              variant="primary"
              size="xs"
              disabled={sendableCount === 0 || sendGate || groupBusy || handlers.groupSendPending}
              aria-label={`Send all ${sendableCount} for ${name}`}
              onClick={() => setConfirmSendOpen(true)}
            >
              {`Send all ${sendableCount}`}
            </Button>
          </div>
        </div>
      </div>

      {open && (
        <div id={panelId} className="flex flex-col gap-3 p-3 pb-0">
          {/* D21 — the Bulk Editor is ALWAYS shown in an open section, even
              with one lead (V1's `isSingleOpenItem` suppression is dropped:
              in this shell the section header, not the card, is what tells a
              rep which play they are editing). */}
          <div className="flex flex-col gap-2">
            <Strong className="text-sm">Bulk Editor</Strong>
            <BaseCopyEditor
              playTypeId={playTypeId}
              playInstanceRef={playInstanceRef}
              playName={name}
              effectiveSubject={play?.invite_subject ?? null}
              effectiveBody={play?.invite_body ?? null}
              senders={senders}
              defaultSender={defaultSender.sender}
              hasEditableCopy={hasEditableCopy}
              copyRequired={copyRequired}
              readOnly={readOnly}
              draftStore={baseCopyDraftStore}
            />
            {/* U3 — the Glaze batch step rung (`Flow step defaults`), which
                shares `useBatchStepBuffer` with the V1 batch step-defaults
                editor rather than re-implementing the 200ms buffer (D6).
                Capability-gated on `ui.has_step_overrides`, never a play-type
                slug. */}
            {gongFlowId !== null && (
              <BulkEditor
                identity={baseCopyDraftStore.identity}
                playKey={playKey}
                flowId={gongFlowId}
                bodyFormat={bodyFormat}
                readOnly={readOnly}
                busy={sending || handlers.groupSendPending || groupBusy}
              />
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Strong className="text-sm">{`Individual edits · ${items.length}`}</Strong>
            {items.map((item, i) => (
              <LeadRow
                key={item.id}
                item={item}
                blockLabel={blockLabels[i]}
                // Change 3 (rail-glaze-parity) — By Play's own section header
                // already names the play (no `playName` passed below, unlike
                // `ByContact.tsx`), so `rowMeta`'s subtitle here would be just
                // `contact_title · account_name` — redundant with the title
                // row directly above it. Hidden so Send centers against a
                // single-line name.
                hideMetaSubtitle
                expanded={expandedItemId === item.id}
                onToggleExpanded={onToggleExpanded}
                readOnly={readOnly}
                sendReadOnly={sendGate}
                isItemBusy={isItemBusy}
                handlers={handlers}
                identity={baseCopyDraftStore.identity}
                baseSender={resetBaseSenderFor(item)}
                sending={sending || handlers.groupSendPending}
                senders={senders}
                plays={plays}
                activePlayTypes={activePlayTypes}
                gongFlowsSuggested={gongFlowsSuggested}
                onReassign={onReassign}
                onClearStepOverrides={onClearStepOverrides}
                requestQueueFlash={requestQueueFlash}
                onRemove={onRemove}
                inputOptions={inputOptionsFor?.(item.id)}
                onSelectInputOption={onSelectInputOption}
                onRefreshInputOptionsIfStale={onRefreshInputOptionsIfStale}
                copyPrefill={copyPrefill}
                senderPrefill={senderPrefillFor(item)}
                onSenderTouched={onSenderTouched}
                hasEditableCopy={hasEditableCopy}
                copyRequired={copyRequired}
                onAutosave={onAutosave}
                onFlushSave={onFlushSave}
                gongFlowId={gongFlowId}
                bodyFormat={bodyFormat}
                onEditStepOverride={onEditStepOverride}
                onResetStepOverride={onResetStepOverride}
                stepOverrideDraftFor={stepOverrideDraftFor}
                stepSaveErrorFor={stepSaveErrorFor}
                subscribeStepOverrides={subscribeStepOverrides}
                onSaveStepOverrides={onSaveStepOverrides}
                batchStepCopy={stagedStepCopy}
                onBeginSendPipeline={onBeginSendPipeline}
                onEndSendPipeline={onEndSendPipeline}
                onUnstick={onUnstick}
                unstickPending={unstickPending}
                onCancel={onCancel}
                cancelPending={cancelPending}
              />
            ))}
          </div>
        </div>
      )}

      {/* Copy verbatim from `PlayQueueCard.tsx:850-904` (D5) — always
          mounted, visibility is the `open` prop, so a closed dialog never
          contributes to the accessibility tree. */}
      <GroupActionConfirmDialog
        open={confirmSendOpen}
        onOpenChange={setConfirmSendOpen}
        title={`Send all ${sendableCount}?`}
        body={
          <>
            <Text size="sm">
              {sendableCount} contacts on {name} will be sent now. This can't be undone.
            </Text>
            {skippedCount > 0 && (
              <Text size="sm" variant="tertiary" className="mt-1">
                {skippedCount} will be skipped.
              </Text>
            )}
          </>
        }
        confirmLabel={handlers.groupSendPending ? 'Sending…' : `Send all ${sendableCount}`}
        confirmLoading={handlers.groupSendPending || groupBusy}
        // A background refresh can drop every remaining item to unsendable
        // while the dialog is already open (E24); without this the confirm
        // would POST `itemIds: []` into `SendSchema`'s `.min(1)`.
        confirmDisabled={sendableCount === 0}
        onConfirm={() => {
          void handlers.handleSendGroup(sendableIds).finally(() => setConfirmSendOpen(false));
        }}
      />
      <GroupActionConfirmDialog
        open={confirmClearOpen}
        onOpenChange={setConfirmClearOpen}
        title={`Clear all ${items.length}?`}
        body={
          <Text size="sm">
            {items.length} contacts on {name} will be removed from the queue. This can't be undone.
          </Text>
        }
        confirmLabel={clearing ? 'Clearing…' : `Clear all ${items.length}`}
        confirmVariant="danger"
        confirmLoading={clearing}
        onConfirm={() => {
          setClearing(true);
          void onBulkRemove(items.map(item => item.id)).finally(() => {
            setClearing(false);
            setConfirmClearOpen(false);
          });
        }}
      />
    </section>
  );
}
